import React, { useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import {
  Box, Button, Paper, TextField, Typography, Alert, Stack,
  InputAdornment, CircularProgress, Link, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAuth } from "../auth/AuthContext";
import { useThemeMode } from "../ThemeModeProvider";
import { homePathForRole, Role } from "../auth/roles";
import { BRAND } from "../theme";

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  useThemeMode(); // live recolour on theme change
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("BUYER");
  const justRegistered = !!(location.state as any)?.registered;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Server role is authoritative; the selected tab is only a fallback when
      // the token/response carry no role, and can never escalate privileges.
      const resolved = await login(username, password, role);
      navigate(homePathForRole(resolved), { replace: true });
    } catch {
      setError("Invalid username or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh", display: "grid", placeItems: "center", p: 2,
        background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`,
      }}
    >
      <Paper elevation={0} sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <Box sx={{ bgcolor: BRAND.greenSoft, color: BRAND.green, width: 56, height: 56, borderRadius: 2, display: "grid", placeItems: "center" }}>
            <StorefrontIcon fontSize="large" />
          </Box>
          <Typography variant="h5">Food Wholesale</Typography>
          <Typography variant="body2" color="text.secondary">Sign in to your wholesale hub</Typography>
        </Stack>

        <ToggleButtonGroup
          value={role} exclusive fullWidth color="primary" size="small"
          onChange={(_e, v: Role | null) => { if (v) setRole(v); }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="BUYER" sx={{ textTransform: "none", fontWeight: 600 }}>
            <PersonOutlineIcon fontSize="small" sx={{ mr: 1 }} />Buyer
          </ToggleButton>
          <ToggleButton value="DISTRIBUTOR" sx={{ textTransform: "none", fontWeight: 600 }}>
            <StorefrontIcon fontSize="small" sx={{ mr: 1 }} />Distributor
          </ToggleButton>
        </ToggleButtonGroup>

        {justRegistered && !error && <Alert severity="success" sx={{ mb: 2 }}>Account created — please sign in.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth label="Email or username" margin="normal" value={username} autoFocus required
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            fullWidth label="Password" type="password" margin="normal" value={password} required
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment> }}
          />

          <Button
            type="submit" fullWidth variant="contained" size="large" disabled={loading}
            sx={{ mt: 2.5, py: 1.2 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Sign in"}
          </Button>
          <Typography variant="body2" align="center" sx={{ mt: 1.5 }}>
            <Link component={RouterLink} to="/forgot-password" underline="hover">Forgot password?</Link>
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Don't have an account?{" "}
          <Link component={RouterLink} to="/register" underline="hover" sx={{ fontWeight: 600 }}>Create one</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginPage;
